import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ExpandedViewComponent } from '@components';
import {
  ChartSize,
  Kpi,
  KpiCategoricalChartData,
  KpiChartConfig,
  KpiChartData,
  KpiChartId,
  KpiKeys,
  KpiNames,
  KpiToggleChartData
} from '@models';
import { ExpandedPanelStateService } from '@services';
import {
  BAR_CHART_OPTION,
  GROUPED_BAR_CHART_OPTION,
  PIE_CHART_COLOR_MAPPING,
  PIE_CHART_OPTION
} from 'app/catalogues/chart.catalogue';
import { KPI_CHART_CONSTANTS } from 'app/catalogues/kpis/kpi-drilldown-charts.catalogue';
import { KPI_TREND_CHART_CUSTOM_TITLES } from 'app/catalogues/kpis/kpi-trend-charts.catalogue';
import { KpiChartFacadeService } from 'app/services/kpi-chart-facade/kpi-chart-facade.service';
import { KpiChartFilterService } from 'app/services/kpi-chart-filter/kpi-chart-filter.service';
import { EChartsOption } from 'echarts';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  startWith,
  withLatestFrom
} from 'rxjs';

@Component({
  selector: 'kpi-charts-expanded-panel',
  templateUrl: './kpi-charts-expanded-panel.component.html',
  styleUrl: './kpi-charts-expanded-panel.component.scss'
})
export class KpiChartsExpandedPanelComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly ROW_THRESHOLD = 5;
  private static readonly LARGE_TABLE_ROWSPAN = 2;
  private static readonly DEFAULT_TABLE_ROWSPAN = 1;
  private static readonly DEFAULT_ROW_HEIGHT = 330;
  private static readonly MAX_ROW_HEIGHT = 674;
  private static readonly FIRST_INCREMENT = 16;
  private static readonly HEIGHT_INCREMENT_PER_ROW = 40;
  private static readonly MIN_CHART_HEIGHT = 180;
  private static readonly CHART_HEIGHT_OFFSET = 50;
  private static readonly TREND_CHART_HEADER_BASE_OFFSET = 28;
  private static readonly TREND_CHART_HEADER_TOGGLE_OFFSET = 16;
  private static readonly TREND_CHART_MAX_EXTRA_HEIGHT = 52;
  private static readonly TREND_CHART_EXTRA_REDUCTION = 35;
  private static readonly TREND_CHART_MIN_HEIGHT = 175;
  private static readonly TREND_CHART_MIN_HEIGHT_WITH_TOGGLE = 190;
  private static readonly GRID_COLUMNS = 2;
  private static readonly GRID_GUTTER_SIZE_PX = 16;
  private static readonly GRID_MIN_ROW_HEIGHT_COMPACT = 290;
  private static readonly COMPACT_VIEW_CHART_EXTRA_REDUCTION = 20;

  private static readonly MIN_GRID_HEIGHT = 269;
  private static readonly MAX_GRID_HEIGHT = 431;
  private static readonly MIN_VIEWPORT_WIDTH = 1200;
  private static readonly REFERENCE_VIEWPORT_WIDTH = 1920;
  private static readonly MIN_VIEWPORT_HEIGHT = 900;
  private static readonly REFERENCE_VIEWPORT_HEIGHT = 1130;
  private static readonly DESKTOP_CONTAINER_HEIGHT_BONUS = 20;
  private static readonly CONTAINER_HEIGHT_EXTRA = 20;
  private expandedPanelStateService: ExpandedPanelStateService = inject(ExpandedPanelStateService);
  private kpiChartFacade: KpiChartFacadeService = inject(KpiChartFacadeService);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);

  kpiChartFilterService = inject(KpiChartFilterService);

  private destroyRef: DestroyRef = inject(DestroyRef);
  private loadingState$ = new BehaviorSubject<boolean>(false);
  private panelBodyResizeObserver?: ResizeObserver;

  @Input() selectedKpiByKey!: { key: KpiKeys; kpi: Kpi };
  @Input() protocolNumber!: string;
  @Input() kpiChartsConfig!: KpiChartConfig[];

  @Output() closeButtonClicked = new EventEmitter<void>();
  @ViewChild('expandedPanelBody', { read: ElementRef })
  expandedPanelBodyRef?: ElementRef<HTMLElement>;

  kpiChartData$!: Observable<KpiChartData | null>;
  displayValues$!: Observable<{
    kpiDisplayValue?: string | number;
    kpiDisplaySecondaryValue?: string;
    kpiDisplayUnit?: string;
  }>;

  bar: EChartsOption = BAR_CHART_OPTION;
  pie: EChartsOption = PIE_CHART_OPTION;
  groupedBar: EChartsOption = GROUPED_BAR_CHART_OPTION;
  colorMap = PIE_CHART_COLOR_MAPPING;
  chartSize: ChartSize = 'medium';

  isLoadingState = false;

  private currentKpiChartData: KpiChartData | null = null;
  private currentDisplayValues: {
    kpiDisplayValue?: string | number;
    kpiDisplaySecondaryValue?: string;
    kpiDisplayUnit?: string;
  } = {};
  reactiveTrendChartData$!: Observable<any>;
  tableRowspan$!: Observable<number>;
  dynamicRowHeight$!: Observable<string>;
  estimatedGridRows$!: Observable<number>;

  // Viewport responsive height properties
  private viewportSize$ = new BehaviorSubject<{ width: number; height: number }>({
    width: window.innerWidth,
    height: window.innerHeight
  });
  responsiveGridRowHeight$!: Observable<string>;
  responsiveChartHeight$!: Observable<string>;
  responsiveChartHeightPx$!: Observable<number>;

  ngOnInit(): void {
    this.kpiChartData$ = this.kpiChartFacade.activeKpiChartData$;
    this.displayValues$ = this.kpiChartFacade.displayValues$;

    this.tableRowspan$ = combineLatest([
      this.kpiChartData$,
      this.kpiChartFacade.isActiveKpiLoading$.pipe(startWith(true))
    ]).pipe(
      map(([chartData, isLoading]) => {
        if (isLoading || !this.kpiChartFacade.hasValidChartData(chartData)) {
          return KpiChartsExpandedPanelComponent.DEFAULT_TABLE_ROWSPAN;
        }

        const rowCount = chartData?.charts.keyMilestonesTable?.rows?.length || 0;
        return rowCount > KpiChartsExpandedPanelComponent.ROW_THRESHOLD
          ? KpiChartsExpandedPanelComponent.LARGE_TABLE_ROWSPAN
          : KpiChartsExpandedPanelComponent.DEFAULT_TABLE_ROWSPAN;
      }),
      distinctUntilChanged()
    );

    const baseTableHeight$ = combineLatest([
      this.kpiChartData$,
      this.kpiChartFacade.isActiveKpiLoading$.pipe(startWith(true))
    ]).pipe(
      map(([chartData, isLoading]) => {
        if (isLoading || !this.kpiChartFacade.hasValidChartData(chartData)) {
          return `${KpiChartsExpandedPanelComponent.DEFAULT_ROW_HEIGHT}px`;
        }

        const rowCount = chartData?.charts.keyMilestonesTable?.rows?.length || 0;

        if (rowCount > KpiChartsExpandedPanelComponent.ROW_THRESHOLD) {
          const extraRows = rowCount - KpiChartsExpandedPanelComponent.ROW_THRESHOLD;
          const calculatedHeight =
            KpiChartsExpandedPanelComponent.DEFAULT_ROW_HEIGHT +
            KpiChartsExpandedPanelComponent.FIRST_INCREMENT +
            Math.max(0, extraRows - 1) * KpiChartsExpandedPanelComponent.HEIGHT_INCREMENT_PER_ROW;

          const finalHeight = Math.min(
            calculatedHeight,
            KpiChartsExpandedPanelComponent.MAX_ROW_HEIGHT
          );
          return `${finalHeight}px`;
        }

        return `${KpiChartsExpandedPanelComponent.DEFAULT_ROW_HEIGHT}px`;
      }),
      distinctUntilChanged()
    );

    this.estimatedGridRows$ = this.tableRowspan$.pipe(
      map((tableRowspan) => this.estimateGridRows(tableRowspan)),
      distinctUntilChanged()
    );

    // Initialize responsive grid row height based on viewport dimensions and parent capacity
    this.responsiveGridRowHeight$ = combineLatest([
      this.viewportSize$,
      this.estimatedGridRows$
    ]).pipe(
      map(([{ width, height }, estimatedRows]) => {
        const responsiveHeight = parseInt(this.calculateResponsiveHeight(width, height), 10);
        const parentFittedHeight = this.calculateParentFittedRowHeight(height, estimatedRows);
        const minRowHeight =
          width <= KpiChartsExpandedPanelComponent.MIN_VIEWPORT_WIDTH
            ? KpiChartsExpandedPanelComponent.GRID_MIN_ROW_HEIGHT_COMPACT
            : KpiChartsExpandedPanelComponent.MIN_GRID_HEIGHT;
        const boostedResponsiveHeight =
          responsiveHeight + KpiChartsExpandedPanelComponent.CONTAINER_HEIGHT_EXTRA;
        const finalHeight = Math.max(
          minRowHeight,
          Math.min(boostedResponsiveHeight, parentFittedHeight)
        );
        return `${finalHeight}px`;
      }),
      distinctUntilChanged()
    );

    this.responsiveChartHeight$ = this.responsiveGridRowHeight$.pipe(
      map((gridHeight) => {
        const heightValue = parseInt(gridHeight.replace('px', ''), 10);
        const compactReduction = this.getCompactViewChartReduction();
        const chartHeight = Math.max(
          KpiChartsExpandedPanelComponent.MIN_CHART_HEIGHT,
          heightValue - KpiChartsExpandedPanelComponent.CHART_HEIGHT_OFFSET - compactReduction
        );
        return `${chartHeight}px`;
      }),
      distinctUntilChanged()
    );

    // Numeric height value for direct inline style binding
    this.responsiveChartHeightPx$ = this.responsiveChartHeight$.pipe(
      map((height: string) => parseInt(height.replace('px', ''), 10)),
      distinctUntilChanged()
    );

    this.dynamicRowHeight$ = combineLatest([
      baseTableHeight$,
      this.responsiveGridRowHeight$,
      this.tableRowspan$
    ]).pipe(
      map(([baseHeight, gridRowHeight, tableRowspan]) => {
        const baseHeightPx = parseInt(baseHeight.replace('px', ''), 10);
        const gridRowHeightPx = parseInt(gridRowHeight.replace('px', ''), 10);
        const tableAvailableHeightPx =
          tableRowspan * gridRowHeightPx +
          (tableRowspan - 1) * KpiChartsExpandedPanelComponent.GRID_GUTTER_SIZE_PX;

        return `${Math.min(baseHeightPx, tableAvailableHeightPx)}px`;
      }),
      distinctUntilChanged()
    );

    this.reactiveTrendChartData$ = combineLatest([
      this.kpiChartData$,
      this.kpiChartFacade.toggleStates$
    ]).pipe(
      map(([chartData, toggleStates]) => {
        if (!chartData?.charts?.trendchart || !this.selectedKpiByKey) {
          return { data: null, trendToggleState: null };
        }

        const trendData = chartData.charts.trendchart;
        const kpiKey = this.selectedKpiByKey.key;
        const kpiToggleStates = toggleStates[kpiKey];

        const trendToggleState = trendData.hasToggle
          ? kpiToggleStates
            ? kpiToggleStates[trendData.identifier as KpiChartId]
            : null
          : null;

        return {
          data: trendData,
          trendToggleState,
          chartId: trendData.identifier
        };
      }),

      distinctUntilChanged((prev, curr) => {
        if (!prev || !curr) return false;

        const dataEqual =
          prev.data?.identifier === curr.data?.identifier &&
          prev.data?.seriesName === curr.data?.seriesName &&
          prev.data?.series?.length === curr.data?.series?.length &&
          prev.data?.isCount === curr.data?.isCount;
        const toggleStateEqual = prev.trendToggleState === curr.trendToggleState;

        return dataEqual && toggleStateEqual;
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      map(({ data, trendToggleState: _ }) => {
        if (!data) return null;

        const trendData = JSON.parse(JSON.stringify(data));

        if (trendData.hasToggle && this.selectedKpiByKey) {
          this.kpiChartFacade.applyTrendChartToggleState(this.selectedKpiByKey.key, trendData);
        }

        return trendData;
      })
    );

    this.kpiChartFacade.isActiveKpiLoading$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        withLatestFrom(this.kpiChartData$.pipe(startWith(null))),
        map(([isLoading, chartData]) => {
          if (isLoading) return true;

          return !this.kpiChartFacade.hasValidChartData(chartData);
        }),
        distinctUntilChanged()
      )
      .subscribe((loadingValue) => {
        this.loadingState$.next(loadingValue);
        this.isLoadingState = loadingValue;
      });

    this.kpiChartData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((chartData) => {
      this.currentKpiChartData = chartData;

      if (chartData) {
        this.syncToggleStatesWithChartData();
      }
    });

    this.displayValues$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((values) => {
      this.currentDisplayValues = values;
    });

    this.kpiChartFacade.toggleStates$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.currentKpiChartData && this.selectedKpiByKey) {
        this.syncToggleStatesWithChartData();
      }
    });
  }

  ngOnDestroy(): void {
    this.loadingState$.complete();
    this.viewportSize$.complete();
    this.panelBodyResizeObserver?.disconnect();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.publishLayoutSize();
  }

  private calculateResponsiveHeight(width: number, height: number): string {
    const {
      MIN_GRID_HEIGHT,
      MAX_GRID_HEIGHT,
      MIN_VIEWPORT_WIDTH,
      REFERENCE_VIEWPORT_WIDTH,
      MIN_VIEWPORT_HEIGHT,
      REFERENCE_VIEWPORT_HEIGHT,
      DESKTOP_CONTAINER_HEIGHT_BONUS
    } = KpiChartsExpandedPanelComponent;

    const viewportWidth =
      width > 0 ? width : typeof window !== 'undefined' ? window.innerWidth : width;
    const viewportHeight =
      height > 0 ? height : typeof window !== 'undefined' ? window.innerHeight : height;

    const widthFactor = Math.max(
      0,
      Math.min(
        1,
        (viewportWidth - MIN_VIEWPORT_WIDTH) / (REFERENCE_VIEWPORT_WIDTH - MIN_VIEWPORT_WIDTH)
      )
    );
    const heightFactor = Math.max(
      0,
      Math.min(
        1,
        (viewportHeight - MIN_VIEWPORT_HEIGHT) / (REFERENCE_VIEWPORT_HEIGHT - MIN_VIEWPORT_HEIGHT)
      )
    );
    const responsiveFactor = Math.min(widthFactor, heightFactor);
    const heightRange = MAX_GRID_HEIGHT - MIN_GRID_HEIGHT;
    const calculatedHeight = MIN_GRID_HEIGHT + heightRange * responsiveFactor;

    let finalHeight = Math.max(
      MIN_GRID_HEIGHT,
      Math.min(MAX_GRID_HEIGHT, Math.round(calculatedHeight))
    );

    const responsiveBonus = Math.round(DESKTOP_CONTAINER_HEIGHT_BONUS * widthFactor);
    finalHeight += responsiveBonus;

    return `${finalHeight}px`;
  }

  private calculateParentFittedRowHeight(parentHeight: number, rowCount: number): number {
    const safeRows = Math.max(1, rowCount);
    const totalGutters = (safeRows - 1) * KpiChartsExpandedPanelComponent.GRID_GUTTER_SIZE_PX;
    const availableHeight = Math.max(0, parentHeight - totalGutters);

    return Math.floor(availableHeight / safeRows);
  }

  private estimateGridRows(tableRowspan: number): number {
    const gridColumns = KpiChartsExpandedPanelComponent.GRID_COLUMNS;
    let currentRow = 0;
    let currentCol = 0;

    for (const chartConfig of this.kpiChartsConfig ?? []) {
      const tile = this.getGridTileDimensions(chartConfig.type, tableRowspan);

      if (tile.colspan >= gridColumns) {
        if (currentCol > 0) {
          currentRow += 1;
          currentCol = 0;
        }
        currentRow += tile.rowspan;
        continue;
      }

      if (currentCol + tile.colspan > gridColumns) {
        currentRow += 1;
        currentCol = 0;
      }

      currentCol += tile.colspan;
      if (currentCol >= gridColumns) {
        currentRow += tile.rowspan;
        currentCol = 0;
      }
    }

    if (currentCol > 0) {
      currentRow += 1;
    }

    return Math.max(1, currentRow);
  }

  private getGridTileDimensions(
    chartType: KpiChartConfig['type'],
    tableRowspan: number
  ): { colspan: number; rowspan: number } {
    if (chartType === 'keyMilestonesTable') {
      return { colspan: 2, rowspan: tableRowspan };
    }

    if (chartType === 'timeCycleChart') {
      return { colspan: 2, rowspan: 1 };
    }

    return { colspan: 1, rowspan: 1 };
  }

  ngAfterViewInit(): void {
    this.startPanelBodyResizeObserver();
    this.publishLayoutSize();
  }

  private startPanelBodyResizeObserver(): void {
    if (typeof ResizeObserver === 'undefined' || !this.expandedPanelBodyRef?.nativeElement) {
      return;
    }

    this.panelBodyResizeObserver = new ResizeObserver(() => {
      this.publishLayoutSize();
    });

    this.panelBodyResizeObserver.observe(this.expandedPanelBodyRef.nativeElement);
  }

  private publishLayoutSize(): void {
    const panelElement = this.expandedPanelBodyRef?.nativeElement as HTMLElement | undefined;

    if (panelElement) {
      const width = Math.round(panelElement.clientWidth);
      const height = Math.round(panelElement.clientHeight);

      if (width > 0 && height > 0) {
        this.viewportSize$.next({ width, height });
        return;
      }
    }

    this.viewportSize$.next({
      width: window.innerWidth,
      height: window.innerHeight
    });
  }

  getTrendChartHeight(baseHeight: number, hasToggle: boolean): number {
    const headerOffset =
      KpiChartsExpandedPanelComponent.TREND_CHART_HEADER_BASE_OFFSET +
      (hasToggle ? KpiChartsExpandedPanelComponent.TREND_CHART_HEADER_TOGGLE_OFFSET : 0);
    const adaptiveMinHeight = Math.max(
      hasToggle
        ? KpiChartsExpandedPanelComponent.TREND_CHART_MIN_HEIGHT_WITH_TOGGLE
        : KpiChartsExpandedPanelComponent.TREND_CHART_MIN_HEIGHT,
      Math.round(baseHeight * (hasToggle ? 0.8 : 0.76))
    );
    const computedHeight =
      baseHeight + headerOffset - KpiChartsExpandedPanelComponent.TREND_CHART_EXTRA_REDUCTION;

    return Math.max(
      adaptiveMinHeight,
      Math.min(
        computedHeight,
        baseHeight + KpiChartsExpandedPanelComponent.TREND_CHART_MAX_EXTRA_HEIGHT
      )
    );
  }

  private getCompactViewChartReduction(): number {
    const { MIN_VIEWPORT_WIDTH, MIN_VIEWPORT_HEIGHT, COMPACT_VIEW_CHART_EXTRA_REDUCTION } =
      KpiChartsExpandedPanelComponent;

    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : MIN_VIEWPORT_WIDTH;
    const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : MIN_VIEWPORT_HEIGHT;

    return viewportWidth <= MIN_VIEWPORT_WIDTH && viewportHeight <= MIN_VIEWPORT_HEIGHT
      ? COMPACT_VIEW_CHART_EXTRA_REDUCTION
      : 0;
  }

  get kpiChartData(): KpiChartData | null {
    return this.currentKpiChartData;
  }

  get kpiDisplayValue(): string | number | undefined {
    return this.currentDisplayValues.kpiDisplayValue;
  }

  get kpiDisplaySecondaryValue(): string | undefined {
    return this.currentDisplayValues.kpiDisplaySecondaryValue;
  }

  get kpiDisplayUnit(): string | undefined {
    return this.currentDisplayValues.kpiDisplayUnit;
  }

  onExpandedPanelCollapse(): void {
    this.expandedPanelStateService.setExpandedPanelExpanded(false);
    this.resetChartsToDefaults();
  }

  onExpandedPanelClose(): void {
    this.expandedPanelStateService.setExpandedPanelExpanded(false);
    this.resetChartsToDefaults();
    this.closeButtonClicked.emit();
  }

  openExpandedView(chartConfig: KpiChartConfig) {
    if (['timeCycleChart', 'keyMilestonesTable'].includes(chartConfig.type)) return;

    const dialogRef = this.dialog.open(ExpandedViewComponent, {
      data: {
        selectedKpi: this.selectedKpiByKey.kpi,
        chartId: chartConfig.id,
        kpiKey: this.selectedKpiByKey.key,
        ...this.getExpandedViewData(chartConfig)
      },
      panelClass: 'fullscreen-dialog',
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh'
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (chartConfig.type === 'toggleChart') {
          this.syncToggleStatesWithChartData();
          this.cdr.detectChanges();
        }
      });
  }

  getBarChartData(chartId: KpiChartId): KpiCategoricalChartData | undefined {
    return this.kpiChartData?.charts.bar?.find(
      (chartData: any) => chartData.identifier === chartId
    );
  }

  getPieChartData(chartId: KpiChartId): KpiCategoricalChartData | undefined {
    return this.kpiChartData?.charts.pie?.find(
      (chartData: any) => chartData.identifier === chartId
    );
  }

  getGroupedBarChartData(chartId: KpiChartId): KpiCategoricalChartData | undefined {
    return this.kpiChartData?.charts.groupedBar?.find(
      (chartData: any) => chartData.identifier === chartId
    );
  }

  getToggleChartData(chartId: KpiChartId): KpiToggleChartData | undefined {
    const originalToggleChartData = this.kpiChartData?.charts.toggleChart?.find(
      (chartData: any) => chartData.identifier === chartId
    );

    if (!originalToggleChartData) return undefined;

    const toggleChartData = JSON.parse(JSON.stringify(originalToggleChartData));

    if (this.selectedKpiByKey) {
      const savedState = this.kpiChartFacade.getToggleState(
        this.selectedKpiByKey.key,
        toggleChartData.identifier
      );

      if (savedState) {
        toggleChartData.charts.primaryChart.selected =
          savedState === toggleChartData.charts.primaryChart.id;
        toggleChartData.charts.secondaryChart.selected =
          savedState === toggleChartData.charts.secondaryChart.id;
      } else {
        toggleChartData.charts.primaryChart.selected = true;
        toggleChartData.charts.secondaryChart.selected = false;
      }
    }

    return toggleChartData;
  }

  private syncToggleStatesWithChartData(): void {
    if (!this.currentKpiChartData || !this.selectedKpiByKey) {
      return;
    }

    let hasChanges = false;

    if (this.currentKpiChartData.charts?.trendchart?.hasToggle) {
      hasChanges = this.syncTrendChartToggleState() || hasChanges;
    }

    if (this.currentKpiChartData.charts?.toggleChart) {
      hasChanges = this.syncToggleChartStates() || hasChanges;
    }

    if (hasChanges) {
      this.cdr.detectChanges();
      this.cdr.markForCheck();
    }

    this.cdr.detectChanges();
  }

  private syncTrendChartToggleState(): boolean {
    const trendChart = this.currentKpiChartData?.charts?.trendchart;
    if (!trendChart?.hasToggle || !this.selectedKpiByKey) {
      return false;
    }

    return this.kpiChartFacade.applyTrendChartToggleState(this.selectedKpiByKey.key, trendChart);
  }

  private syncToggleChartStates(): boolean {
    const toggleCharts = this.currentKpiChartData?.charts?.toggleChart;
    if (!toggleCharts || !this.selectedKpiByKey) {
      return false;
    }

    const kpiKey = this.selectedKpiByKey.key;
    let hasChanges = false;

    toggleCharts.forEach((toggleChart) => {
      const savedState = this.kpiChartFacade.getToggleState(kpiKey, toggleChart.identifier);

      if (savedState) {
        const wasSelected = toggleChart.charts.primaryChart.selected;

        toggleChart.charts.primaryChart.selected =
          savedState === toggleChart.charts.primaryChart.id;
        toggleChart.charts.secondaryChart.selected =
          savedState === toggleChart.charts.secondaryChart.id;

        if (wasSelected !== toggleChart.charts.primaryChart.selected) {
          hasChanges = true;
        }
      }
    });

    return hasChanges;
  }

  private getExpandedViewData(chartConfig: KpiChartConfig) {
    switch (chartConfig.type) {
      case 'trendchart':
        return this.getTrendChartExpandedData();
      case 'bar':
        return this.getBarChartExpandedData(chartConfig.id);
      case 'pie':
        return this.getPieChartExpandedData(chartConfig.id);
      case 'toggleChart':
        return this.getToggleChartExpandedData(chartConfig.id);
      case 'groupedBar':
        return this.getGroupedBarChartExpandedData(chartConfig.id);
      default:
        return;
    }
  }

  private getTrendChartExpandedData() {
    const trendChart = this.kpiChartData?.charts.trendchart;
    if (!trendChart) return;
    return {
      viewTitle: this.formatChartTitle(trendChart.titleSuffix, trendChart.identifier),
      chartTitle: 'TrendLine',
      chartType: 'trend',
      expandedTrendChartData: trendChart,
      viewDescription: trendChart.tooltipText
    };
  }

  private getBarChartExpandedData(chartId: KpiChartId) {
    const barChartData = this.getBarChartData(chartId);
    if (!barChartData) return;
    return {
      viewTitle: this.formatChartTitle(barChartData.titleSuffix, barChartData.identifier),
      chartTitle: 'BarChart',
      chartType: 'bar',
      expandedBarChartData: barChartData,
      viewDescription: barChartData.tooltipText,
      fieldFilter: barChartData.fieldFilter,
      filterService: this.kpiChartFilterService,
      hasXaxisAggLabel: barChartData.hasXAxisAggLabel
    };
  }

  private getPieChartExpandedData(chartId: KpiChartId) {
    const pieChartData = this.getPieChartData(chartId);
    if (!pieChartData) return;
    return {
      viewTitle: this.formatChartTitle(pieChartData.titleSuffix, pieChartData.identifier),
      chartTitle: 'PieChart',
      chartType: 'pie',
      expandedPieChartData: pieChartData,
      viewDescription: pieChartData.tooltipText,
      fieldFilter: pieChartData.fieldFilter,
      filterService: this.kpiChartFilterService
    };
  }

  private getGroupedBarChartExpandedData(chartId: KpiChartId) {
    const groupedBarChartData = this.getGroupedBarChartData(chartId);
    if (!groupedBarChartData) return;
    return {
      viewTitle: this.formatChartTitle(
        groupedBarChartData.titleSuffix,
        groupedBarChartData.identifier
      ),
      chartTitle: 'GroupedBarChart',
      chartType: 'groupedBar',
      expandedBarChartData: groupedBarChartData,
      viewDescription: groupedBarChartData.tooltipText,
      fieldFilter: groupedBarChartData.fieldFilter,
      filterService: this.kpiChartFilterService
    };
  }

  private getToggleChartExpandedData(chartId: KpiChartId) {
    const toggleChartData = this.getToggleChartData(chartId);
    if (!toggleChartData) return;
    return {
      viewTitle: this.formatChartTitle(toggleChartData.titleSuffix, toggleChartData.identifier),
      chartTitle: 'ToggleChart',
      chartType: 'toggle',
      toggleChartData: toggleChartData,
      viewDescription: toggleChartData.charts.primaryChart.selected
        ? toggleChartData.charts.primaryChart.data.tooltipText
        : toggleChartData.charts.secondaryChart.data.tooltipText,
      filterService: this.kpiChartFilterService
    };
  }

  private createReactiveChartStream<T>(
    chartIdentifier: KpiChartId,
    chartDataSelector: (chartData: KpiChartData) => T | null
  ): Observable<T | null> {
    return combineLatest([this.kpiChartData$, this.kpiChartFacade.toggleStates$]).pipe(
      map(([chartData, toggleStates]) => {
        if (!chartData || !this.selectedKpiByKey) {
          return { data: null, toggleState: null, chartId: chartIdentifier };
        }

        const selectedChartData = chartDataSelector(chartData);
        const kpiKey = this.selectedKpiByKey.key;
        const kpiToggleStates = (toggleStates[kpiKey] as Record<KpiChartId, KpiChartId>) || {};
        const toggleState = kpiToggleStates[chartIdentifier];

        return {
          data: selectedChartData,
          toggleState,
          chartId: chartIdentifier
        };
      }),
      distinctUntilChanged((prev, curr) => {
        if (!prev || !curr) return false;

        const dataEqual = prev.data === curr.data;
        const toggleStateEqual = prev.toggleState === curr.toggleState;
        return dataEqual && toggleStateEqual;
      }),
      map(({ data }) => data)
    );
  }

  getReactiveToggleChartData(chartId: KpiChartId): Observable<KpiToggleChartData | null> {
    return this.createReactiveChartStream(chartId, (chartData) => {
      const toggleChart = chartData.charts?.toggleChart?.find(
        (chart: any) => chart.identifier === chartId
      );

      if (!toggleChart) return null;

      const reactiveToggleChart = {
        ...toggleChart,
        charts: {
          ...toggleChart.charts,
          primaryChart: { ...toggleChart.charts.primaryChart },
          secondaryChart: { ...toggleChart.charts.secondaryChart }
        }
      };

      if (this.selectedKpiByKey) {
        const savedState = this.kpiChartFacade.getToggleState(this.selectedKpiByKey.key, chartId);

        if (savedState) {
          reactiveToggleChart.charts.primaryChart.selected =
            savedState === reactiveToggleChart.charts.primaryChart.id;
          reactiveToggleChart.charts.secondaryChart.selected =
            savedState === reactiveToggleChart.charts.secondaryChart.id;
        } else {
          reactiveToggleChart.charts.primaryChart.selected = true;
          reactiveToggleChart.charts.secondaryChart.selected = false;
        }
      }

      reactiveToggleChart.tooltipText = reactiveToggleChart.charts.primaryChart.selected
        ? reactiveToggleChart.charts.primaryChart.data.tooltipText
        : reactiveToggleChart.charts.secondaryChart.data.tooltipText;

      return reactiveToggleChart;
    });
  }

  getReactiveBarChartData(chartId: KpiChartId): Observable<KpiCategoricalChartData | null> {
    return this.createReactiveChartStream(chartId, (chartData) => {
      return chartData.charts?.bar?.find((chart: any) => chart.identifier === chartId) || null;
    });
  }

  getReactivePieChartData(chartId: KpiChartId): Observable<KpiCategoricalChartData | null> {
    return this.createReactiveChartStream(chartId, (chartData) => {
      return chartData.charts?.pie?.find((chart: any) => chart.identifier === chartId) || null;
    });
  }

  formatChartTitle(titleSuffix: string, chartId: KpiChartId): string {
    if (chartId === 'trendChart') {
      const customTitle = KPI_TREND_CHART_CUSTOM_TITLES[this.selectedKpiByKey.key];
      if (customTitle) {
        return customTitle;
      }
    }

    const chartConstants = KPI_CHART_CONSTANTS[chartId];
    if (chartConstants?.customChartTitle) {
      return chartConstants.customChartTitle;
    }

    // Default title formatting
    const kpiName: KpiNames = this.selectedKpiByKey.kpi.name;
    let suffix = '';
    suffix += titleSuffix.toLowerCase();

    return `${kpiName} ${suffix}`;
  }

  private resetChartsToDefaults(): void {
    if (!this.currentKpiChartData || !this.selectedKpiByKey) {
      return;
    }

    this.kpiChartFacade.resetToggleStatesForKpi(
      this.selectedKpiByKey.key,
      this.currentKpiChartData
    );
    this.syncToggleStatesWithChartData();
    this.cdr.detectChanges();
  }

  public resetAllChartsToDefaults(): void {
    this.resetChartsToDefaults();
  }

  public resetToggleChartsToDefaults(): void {
    if (!this.currentKpiChartData || !this.selectedKpiByKey) {
      return;
    }

    if (this.currentKpiChartData.charts?.toggleChart) {
      this.currentKpiChartData.charts.toggleChart.forEach((toggleChart) => {
        this.kpiChartFacade.setToggleState(
          this.selectedKpiByKey.key,
          toggleChart.identifier,
          toggleChart.charts.primaryChart.id
        );
      });
    }
    this.syncToggleStatesWithChartData();
    this.cdr.detectChanges();
  }

  public resetTrendChartToDefault(): void {
    if (!this.currentKpiChartData || !this.selectedKpiByKey) {
      return;
    }

    if (this.currentKpiChartData.charts?.trendchart?.hasToggle) {
      const trendChart = this.currentKpiChartData.charts.trendchart;
      const defaultState = trendChart.identifier + '_percent';
      this.kpiChartFacade.setToggleState(
        this.selectedKpiByKey.key,
        trendChart.identifier,
        defaultState as KpiChartId
      );
    }
    this.syncToggleStatesWithChartData();
    this.cdr.detectChanges();
  }
}
